import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import { CONSTANTS } from "@constants/index";
import { useAddGuests } from "@hooks/useAddGuests/useAddGuests";
import { usePinInput } from "@hooks/usePinInput";
import { BookingFetchResponseType } from "@models/services/booking";
import { SelectGuestType } from "@models/shared/shared.model";
import handleResponse from "@services/apiConfig/api.response.service";
import API_SERVICES from "@services/index";
import authSlice from "@slices/auth.slice";
import kioskSlice from "@slices/kiosk.slice";
import localSlice from "@slices/local.slice";
import { RootState } from "@store/index";
import { trackLoginSucceeded } from "@utils/analytics";
import commonUtils from "@utils/common.utils";

export const useUserLogin = ({
  slotInfo,
}: {
  slotInfo: BookingFetchResponseType;
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isPinChecking, setIsPinChecking] = useState(false);
  const [pinCheckError, setPinCheckError] = useState<string | null>(null);
  const [loginError, setLoginError] = useState<string | null>(null);
  const [selectedGuest, setSelectedGuest] = useState<SelectGuestType | null>(
    null
  );

  const { actions, data, setters } = useAddGuests({
    hideLiteProfile: 0,
    slotInfo,
  });

  const showLoginModal = useSelector(
    (state: RootState) => state.kiosk.showLoginModal
  );
  const { authModal } = useSelector((state: RootState) => state.auth);

  const { t } = useTranslation();
  const dispatch = useDispatch();

  const {
    handlePinChange,
    handlePinKeyDown,
    handlePinPaste,
    isPinComplete,
    pin,
    pinInputRefs,
    resetPin,
  } = usePinInput({ length: 6 });

  const pinRef = useRef(pin);
  useEffect(() => {
    pinRef.current = pin;
  }, [pin]);

  const setIsModalOpen = (value: boolean) => {
    dispatch(kioskSlice.actions.setShowLoginModal(value));
    if (!value) {
      dispatch(kioskSlice.actions.setPendingSlotSelection(null));
      setSelectedGuest(null);
      setPinCheckError(null);
      setLoginError(null);
    }
  };

  const closeAfterLogin = () => {
    dispatch(kioskSlice.actions.setShowLoginModal(false));
    setSelectedGuest(null);
    setPinCheckError(null);
    setLoginError(null);
  };

  const handleGuestChange = useCallback(
    async (guest: SelectGuestType | null) => {
      setSelectedGuest(guest);
      setPinCheckError(null);
      setLoginError(null);

      if (!guest?.profileUuid) return;

      setIsPinChecking(true);
      try {
        const data = await API_SERVICES.KIOSK.CHECK_USER_PIN(guest.profileUuid);
        if (!data?.ok) {
          setPinCheckError(commonUtils.getErrorMessage(data));
        }
      } catch {
        setPinCheckError(null);
      } finally {
        setIsPinChecking(false);
      }
    },
    []
  );

  useEffect(() => {
    if (showLoginModal) {
      resetPin();
    } else {
      resetPin(false);
      setSelectedGuest(null);
    }
  }, [showLoginModal]);

  useEffect(() => {
    const isPinEmpty = pin.every(val => !val);

    if (!selectedGuest && !isPinEmpty) {
      resetPin();
    }
  }, [selectedGuest, pin]);

  const loginCall = useCallback(async () => {
    setIsLoading(true);

    const data = await API_SERVICES.KIOSK.LOGIN_USER(
      selectedGuest?.profileUuid || "",
      pinRef.current.join("")
    );
    const responseData = handleResponse(data, null, false);

    if (responseData?.ok) {
      dispatch(kioskSlice.actions.setIsSlotsLoading(true));

      dispatch(
        authSlice.actions.passAuth({ ...authModal, loginCompleted: true })
      );
      dispatch(
        localSlice.actions.userAuthenticated({
          isAuthenticated: true,
          refresh_token: responseData.data.refresh_token,
          token: responseData.data.token,
        })
      );

      trackLoginSucceeded({
        email: selectedGuest?.email,
        userId: selectedGuest?.profileUuid,
      });

      commonUtils.toastService(
        t("successfullLogin"),
        CONSTANTS.TOAST_TYPE.SUCCESS
      );

      closeAfterLogin();
    } else {
      setLoginError(commonUtils.getErrorMessage(responseData));
    }
    resetPin();
    setIsLoading(false);
    return data;
  }, [selectedGuest]);

  const handleLogin = async (e: React.FormEvent) => {
    if (isLoading || !isPinComplete || !selectedGuest) {
      return;
    }
    e.preventDefault();
    setLoginError(null);
    setIsLoading(true);

    try {
      await loginCall();
    } catch (error) {
      console.error("Login failed:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return {
    clearAddGuestError: () => setters.setAddGuestError(""),
    actions,
    data,
    // Guest change with pin check
    handleGuestChange,
    // Form
    handleLogin,
    handlePinChange,
    handlePinKeyDown,
    handlePinPaste,
    // State
    isLoading,
    isPinChecking,
    isPinComplete,
    loadOptions: actions.loadOptions,
    // Errors
    loginError,
    // PIN
    pin,
    pinCheckError,
    pinInputRefs,
    selectedGuest,
    // Modal
    setIsModalOpen,
    // Guest
    setSelectedGuest,
    setters,
    showLoginModal,
  };
};
